/* Copyright (c) 2020-present CloudIO Inc */

import { getFilterDataType } from '@cloudio-saas/datasource-types';
import { emptyFunction } from '@cloudio-saas/statex';
import { ExpressionReturnType } from 'layout/Sidebar/panels/PropertiesView/property-editors/types';
import { isNotEmpty } from 'utils/isEmpty';
import { page as SamplePage } from '../../help/sample_pages/ActionCreatePage';
import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { AnyItem, ItemType, Page } from '../../registry/ComponentRegistry';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSet';

export async function returnType({
  appUid,
  pageId,
  itemId,
  attribute,
  platform,
  itemType,
  storeType,
}: {
  appUid: string;
  pageId: string;
  itemId: string;
  attribute: string;
  platform: Platform;
  itemType: ItemType;
  storeType: 'QUERY_FILTER' | 'STORE';
}): Promise<ExpressionReturnType> {
  if (attribute) {
    const aliasAndEmbeddedPageIdValue = platform.getDataSourceAlias(
      appUid,
      pageId,
      itemId,
      storeType,
    );

    if (!aliasAndEmbeddedPageIdValue) {
      return 'any';
    }

    const store = isNotEmpty(aliasAndEmbeddedPageIdValue.alias)
      ? platform.getStore(appUid, pageId, aliasAndEmbeddedPageIdValue.alias)
      : undefined;

    if (!store) {
      platform.showError(
        `Missing parent store for ${itemType} with itemId: ${itemId} in page ${pageId}!`,
        { appUid, pageId, itemId },
      );
      return 'any';
    }
    await store.initializeAttributes();
    const attr = store.getAttribute(attribute);
    if (!attr) {
      platform.showError(
        `Invalid attribute '${attribute}' specified in ${itemType} with itemId: ${itemId}!`,
        { appUid, pageId, itemId },
      );
      return 'any';
    }
    const normalizedAttributeType = getFilterDataType(attr);
    if (normalizedAttributeType === 'Number') {
      return 'number';
    }
    if (normalizedAttributeType === 'Boolean') {
      return 'boolean';
    }
    if (normalizedAttributeType === 'Date') {
      return 'date';
    }
    if (normalizedAttributeType === 'YN') {
      return 'yn';
    }
    if (normalizedAttributeType === 'TF') {
      return 'tf';
    }
    if (normalizedAttributeType === 'JSON') {
      return 'object';
    }
    return 'string';
  }
  return 'any';
}

const ActionSetManager: ActionManagerFn<'ActionSet'> = (
  platform: Platform,
): ActionManager<'ActionSet'> => {
  let callback = emptyFunction;
  return {
    ...baseActionManager(platform, 'ActionSet'),
    defaultValue,
    props: {
      ...baseActionProps,
      attribute: {
        label: 'Attribute',
        type: 'ViewAttributeChooser',
        required: true,
        aliasType: 'STORE',
        getAttributeDataTypes: () => 'ALL',
        preventMultiEdit: true,
      },
      value: {
        label: 'Value',
        type: 'expression',
        refreshCallback: (cb) => {
          callback = cb;
        },
        returnType: async (
          item: RequiredItem<'ActionSet'>,
          { appUid, pageId, itemId },
        ) => {
          return returnType({
            appUid,
            pageId,
            itemId,
            attribute: item.props.attribute,
            itemType: item.type,
            platform,
            storeType: 'STORE',
          });
        },
        required: true,
        helpText: 'The value must match the data type of the above attribute.',
      },
    },
    onPropertyChange: (property, value) => {
      if (property === 'attribute') {
        callback();
        return {
          [property]: value,
          value: defaultValue.value,
        };
      }
      return {
        [property]: value,
      };
    },
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'value':
          return !!props.attribute;
        default:
          return true;
      }
    },
    name: 'Set',
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return (
        parent.type === 'ActionCreate' ||
        parent.type === 'ActionDuplicateRecord' ||
        parent.type === 'TriggerBeforeCreate' ||
        parent.type === 'ActionCopyRecordInLoop' ||
        parent.type === 'ActionLoopRecords'
      );
    },
    getDisplayText: (item) => item.props.attribute,
    icon: { iconName: 'value-absolute' },
    description: `**Overview**<br/>
The "Set" action is a precise data manipulation component designed to assign a value to a specific attribute (field) within a data record. Unlike a generic variable setter, this action is strictly context-aware and operates directly on the record currently being processed.<br/><br/>
This component is structurally restricted and cannot exist independently. It is designed solely to define field values during specific lifecycle events: creating a new record, duplicating a record, or iterating through existing records in a loop. It serves as the mechanism for initialization (setting defaults) or batch modification.

**Key Features**<br/>
* **Strict Parent Scope:** Explicitly restricted via isAllowedInsideParent to function only within specific parent containers: Create Record, Duplicate Record, Loop Records, Copy Record In Loop, and Trigger Before Create.
* **Smart Type Validation:** Implements advanced logic (returnType function) to dynamically enforce data type safety. If the selected Attribute is a "Date", the Value expression is restricted to return a Date type; if it is a "Boolean", it expects a Boolean, ensuring data integrity before runtime.
* **Contextual Dependency:** The available attributes are determined by the Data Store associated with the parent action (e.g., the store being looped over or the store where a record is being created).
* **Dynamic Assignment:** The value property supports expressions, allowing fields to be set based on calculations, other variables, or conditional logic (e.g., Status = If(Age > 18, 'Adult', 'Minor')).

**Properties**<br/>
* **Attribute (Required):** A ViewAttributeChooser property.
  * *Function:* Selects the specific field in the record to be modified.
  * *Behavior:* Changing this property resets the value property to ensure type compatibility.
* **Value (Required):** An expression property.
  * *Function:* Defines the data to be written to the selected attribute.
  * *Validation:* The expected return type changes dynamically based on the selected attribute (e.g., Number, Boolean, Date, JSON).

**Use Cases**<br/>
* **Default Values:** Inside an ActionCreateRecord, using this action to set the "Created Date" to Now() and "Status" to 'New' automatically.
* **Batch Updates:** Inside an ActionLoopRecords, using this action to set the "IsProcessed" flag to true for every record iterated over.
* **Data Cloning:** Inside an ActionDuplicateRecord, using this action to overwrite specific fields (like resetting "ApprovedBy" to null) while keeping the rest of the copied data intact.`,
    helpRenders: [
      {
        title: 'Action Set',
        description:
          "Try changing the props and press the below 'Inovke Action' button to see it in action! Press the 'Reset' button to remove the newly added rows from the store.",
        page: SamplePage,
        height: 300,
        exampleId: 'e1',
        clearParentChildren: true,
        visibleProps: ['attribute', 'value'],
        item: {
          children: [],
          itemId: 'actionSet',
          parent: 'actionCreate',
          props: {
            attribute: 'name',
            value: {
              type: 'text',
              value: 'Test',
            },
          },
          type: 'ActionSet',
        },
      },
      {
        title: 'Value Expression',
        description:
          'Use expression to set any non string value. The below example is setting a value of true (boolean) to Active attribute which is of type Boolean',
        page: SamplePage,
        height: 300,
        exampleId: 'e2',
        clearParentChildren: true,
        visibleProps: ['attribute', 'value'],
        item: {
          children: [],
          itemId: 'actionSet',
          parent: 'actionCreate',
          props: {
            attribute: 'active',
            value: {
              type: 'expression',
              value: 'true',
            },
          },
          type: 'ActionSet',
        },
      },
    ],
  };
};

export default ActionSetManager;
